/**
 * The company columns the network export can emit, and what a ticked set
 * serialises to.
 *
 * Shared by both frontends: the v1 and v2 export dialogs must agree on this, and
 * a divergence between two copies would hide here rather than fail loudly.
 */
export type ExportColumnGroup =
  | 'identity'
  | 'location'
  | 'relationship'
  | 'activity';

export interface ExportColumnDef {
  id: string;
  group: ExportColumnGroup;
  /**
   * Basic adds this column even when the network table does not show it — the
   * six that make up the difference between the 19-column table and the
   * 26-column standard export. Not mandatory; the picker can drop any of them.
   */
  inBasicPreset?: true;
  /**
   * The data point backing this column; absent means platform metadata (id,
   * timestamp, count). Not derivable from `id` — `name` is `display_name`.
   */
  dataPointId?: string;
}

export const EXPORT_COLUMN_GROUPS: ExportColumnGroup[] = [
  'identity',
  'location',
  'relationship',
  'activity',
];

/**
 * Mirrors `COLUMN_TITLES` in the export job, minus its two `_NAME` aliases. Ids
 * stay camelCase. Ordered for the *picker* — the sheet's order lives in
 * `COLUMN_TITLES` and is deliberately different.
 */
export const NETWORK_EXPORT_COLUMNS: readonly ExportColumnDef[] = [
  { id: 'id', group: 'identity', inBasicPreset: true },
  { id: 'name', group: 'identity', dataPointId: 'display_name' },
  { id: 'officialName', group: 'identity', dataPointId: 'official_name' },
  {
    id: 'internalCompanyCode',
    group: 'identity',
    dataPointId: 'internal_company_code',
  },
  { id: 'phone', group: 'identity', dataPointId: 'phone' },
  { id: 'facEmail', group: 'identity', dataPointId: 'fac_email' },

  { id: 'country', group: 'location', dataPointId: 'country' },
  { id: 'city', group: 'location', dataPointId: 'city' },
  {
    id: 'addressLine1',
    group: 'location',
    inBasicPreset: true,
    dataPointId: 'address_line_1',
  },
  {
    id: 'addressLine2',
    group: 'location',
    inBasicPreset: true,
    dataPointId: 'address_line_2',
  },
  {
    id: 'zipCode',
    group: 'location',
    inBasicPreset: true,
    dataPointId: 'zip_code',
  },
  {
    id: 'geojson',
    group: 'location',
    inBasicPreset: true,
    dataPointId: 'geojson',
  },

  {
    id: 'connectionType',
    group: 'relationship',
    dataPointId: 'connection_type',
  },
  {
    id: 'relationshipType',
    group: 'relationship',
    dataPointId: 'relationship_type',
  },
  {
    id: 'collaborationStatus',
    group: 'relationship',
    dataPointId: 'collaboration_status',
  },
  {
    id: 'collaborationStartDate',
    group: 'relationship',
    dataPointId: 'collaboration_start_date',
  },
  {
    id: 'collaborationEndDate',
    group: 'relationship',
    dataPointId: 'collaboration_end_date',
  },
  {
    id: 'supplyChainTier',
    group: 'relationship',
    dataPointId: 'supply_chain_tier',
  },
  { id: 'vendors', group: 'relationship', dataPointId: 'vendors' },
  { id: 'agents', group: 'relationship', dataPointId: 'agents' },
  {
    id: 'responsiblePerson',
    group: 'relationship',
    dataPointId: 'responsible_person',
  },
  { id: 'connectionDate', group: 'relationship', inBasicPreset: true },

  {
    id: 'facilityProcesses',
    group: 'activity',
    dataPointId: 'facility_processes',
  },
  {
    id: 'retracedRiskScore',
    group: 'activity',
    dataPointId: 'retraced_risk_score',
  },
  { id: 'totalUsersCount', group: 'activity' },
  { id: 'activeUsersCount', group: 'activity' },
  { id: 'notes', group: 'activity', dataPointId: 'notes' },
  { id: 'creationDate', group: 'activity', inBasicPreset: true },
  { id: 'updatedAt', group: 'activity' },
];

/** The one column a selection cannot drop: every row is reconciled on it. */
export const MANDATORY_EXPORT_COLUMN_IDS = ['id'];

/** The ids a user can actually tick — everything but the company id. */
export const SELECTABLE_EXPORT_COLUMN_IDS = NETWORK_EXPORT_COLUMNS.filter(
  column => !MANDATORY_EXPORT_COLUMN_IDS.includes(column.id),
).map(column => column.id);

/** The columns Basic adds beyond the ones the network table itself shows. */
export const BASIC_PRESET_EXTRA_COLUMN_IDS = NETWORK_EXPORT_COLUMNS.filter(
  column => column.inBasicPreset,
).map(column => column.id);

/**
 * The authoritative column list a request sends. No "empty means everything"
 * sentinel, so an export can be one column wide and mean it.
 */
export function buildColumnKeys(selected: ReadonlySet<string>): string[] {
  return NETWORK_EXPORT_COLUMNS.filter(
    column =>
      MANDATORY_EXPORT_COLUMN_IDS.includes(column.id) ||
      selected.has(column.id),
  ).map(column => column.id);
}

/**
 * What Basic sends: the visible table columns plus the `inBasicPreset` six,
 * which appear in no table column. Without the union Basic drops 26 -> 20.
 */
export function basicColumnKeys(visibleIds: readonly string[]): string[] {
  return buildColumnKeys(
    new Set([...visibleIds, ...BASIC_PRESET_EXTRA_COLUMN_IDS]),
  );
}

/**
 * How many columns the sheet will have. Mirrors `buildSheetColumns`; the preset
 * card and footer both read it so they cannot disagree.
 */
export function exportedColumnCount(keys: readonly string[]): number {
  const selected = new Set(keys);
  return NETWORK_EXPORT_COLUMNS.filter(
    column =>
      MANDATORY_EXPORT_COLUMN_IDS.includes(column.id) ||
      selected.has(column.id),
  ).length;
}
